import { useCallback, useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import PrintButtons from "@/components/PrintButtons";
import CancelReceiptView from "@/components/CancelReceiptView";
import { useStations } from "@/components/SearchForm";
import { api } from "@/lib/api";
import { money } from "@/lib/format";

const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

export default function CancellationHistory({ showCancelledBy = true }) {
  const stations = useStations();
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(daysAgo(0));
  const [station, setStation] = useState("all");
  const [list, setList] = useState([]);
  const [print, setPrint] = useState(null);
  const load = useCallback(() => {
    api.get("/cancellations/history", { params: { from_date: from, to_date: to, station_id: station === "all" ? "" : station } })
      .then((r) => setList(r.data)).catch(() => setList([]));
  }, [from, to, station]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="cancellation-history">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs font-semibold text-slate-600">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="history-from-input" /></div>
        <div><Label className="text-xs font-semibold text-slate-600">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="history-to-input" /></div>
        <div className="min-w-[200px]">
          <Label className="text-xs font-semibold text-slate-600">Terminal</Label>
          <Select value={station} onValueChange={setStation}>
            <SelectTrigger data-testid="history-terminal-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All terminals</SelectItem>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.city} - {s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <span className="pb-2 text-sm text-slate-500">{list.length} cancellation(s)</span>
      </div>
      <div className="card-soft divide-y divide-slate-100">
        {list.map((b) => (
          <div key={b.id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`history-row-${b.code}`}>
            <span className="font-mono font-bold">{b.code}</span>
            <span className="min-w-0 flex-1">
              <b>{b.cancel_reason}</b>
              <div className="text-xs text-slate-500">
                {b.trip?.origin_city} → {b.trip?.destination_city} · {new Date(b.cancelled_at).toLocaleString("en-GB")}
                {showCancelledBy ? ` · by ${b.cancelled_by} (${(b.cancelled_by_role || "").replace("_", " ")})` : ""}
              </div>
            </span>
            <span className="text-xs">Charge {money(b.cancel_fee || 0)} · Refund <b>{money(b.refund_amount || 0)}</b></span>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setPrint(b)} data-testid={`history-print-${b.code}`}><Printer className="h-4 w-4" />Print</Button>
          </div>
        ))}
        {!list.length && <p className="p-6 text-center text-sm text-slate-500">No cancellations in this range.</p>}
      </div>
      <Dialog open={!!print} onOpenChange={(o) => !o && setPrint(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-xl" data-testid="history-print-dialog">
          <DialogHeader><DialogTitle className="font-display">Cancellation receipt</DialogTitle></DialogHeader>
          {print && <><PrintButtons testPrefix="history-receipt-print">{(f) => <CancelReceiptView booking={print} format={f} />}</PrintButtons><CancelReceiptView booking={print} format="a4" /></>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
